import { ColumnDef } from "@tanstack/react-table";
import { ClockIcon, CrossCircleIcon } from "assets/icons";
import cn from "classnames";
import { useBoolean, useLanguage } from "hooks";
import { useProfile } from "modules/common";
import moment from "moment";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  ItemsTable,
  PersonInfo,
  TableItemAction,
  TableItemActionsControl
} from "ui";

import {
  EARNING_TYPE_LABELS,
  EarningChapters,
  EarningType
} from "../../constants";
import { EarningsListItem } from "../../financial.types";
import { PayEarningsModal, CancelEarningsModal } from "../modals";
import { EarningsTableProps } from "./EarningsTable.types";

import styles from "./EarningsTable.module.scss";

const EarningsTable = ({
  earnings,
  selectedEarningsIds,
  isLoading,
  sorting,
  setSorting,
  setSelectedEarningsIds,
  refetchEarnings
}: EarningsTableProps) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { user } = useProfile({ enabled: false });

  const { activeTab } = useParams();

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const [confirmingEarningId, setConfirmingEarningId] = useState<string | null>(
    null
  );

  const {
    value: isCancelEarningsModalOpen,
    setTrue: openCancelEarningsModal,
    setFalse: closeCancelEarningsModal
  } = useBoolean(false);

  const {
    value: isPayEarningsModalOpen,
    setTrue: openPayEarningsModal,
    setFalse: closePayEarningsModal
  } = useBoolean(false);

  const columns: Array<ColumnDef<EarningsListItem, EarningsListItem[]>> =
    useMemo(() => {
      const baseColumns: Array<
        ColumnDef<EarningsListItem, EarningsListItem[]>
      > = [
        {
          id: "resellerFullName",
          header: t("Reseller"),
          size: 20,
          minSize: 20,
          cell: ({ row }) => (
            <PersonInfo
              image={earnings[row.index].reseller.image}
              personName={`${earnings[row.index].reseller.firstname} ${earnings[row.index].reseller.lastname}`}
              bottomText={`${earnings[row.index].reseller.id}`}
              nameClassName={styles.table_resellerName}
              bottomTextClassName={styles.table_resellerId}
              dimensions={{ width: 32, height: 32, borderRadius: "100%" }}
            />
          )
        },
        {
          id: "orderId",
          header: t("Order"),
          size: 5,
          minSize: 5,
          cell: ({ row }) => earnings[row.index].orderId
        },
        {
          id: "type",
          header: t("Type"),
          size: 8,
          minSize: 8,
          cell: ({ row }) => {
            const earningType = earnings[row.index].type;

            return (
              <span
                className={cn(styles.table_type, {
                  [styles.table_type__referral]:
                    earningType === EarningType.ProductsResellingByReferral
                })}
              >
                {t(EARNING_TYPE_LABELS[earningType] ?? "—")}
              </span>
            );
          }
        },
        {
          id: "amount",
          accessorKey: "amount",
          header: t("Earnings"),
          size: 5,
          minSize: 5,
          cell: ({ row }) => earnings[row.index].amount
        },
        {
          id: "mobileMoneyNumber",
          header: t("Mobile Money"),
          size: 5,
          minSize: 5,
          cell: ({ row }) => earnings[row.index].mobileMoneyNumber
        },
        {
          id: "createdAt",
          accessorKey: "createdAt",
          header: t("Date"),
          size: 5,
          minSize: 5,
          cell: ({ row }) => (
            <pre>
              {`${moment(earnings[row.index].createdAt).format("DD/MM/YYYY")}`}
              {"\n"}
              {`${moment(earnings[row.index].createdAt).format("HH:mm")}`}
            </pre>
          )
        }
      ];

      const pendingTabColumns: Array<
        ColumnDef<EarningsListItem, EarningsListItem[]>
      > = [
        {
          id: "select",
          header: () => {
            const isChecked = earnings.some((earning) =>
              selectedEarningsIds?.includes(earning.id)
            );
            const isFullyChecked = earnings.every((earning) =>
              selectedEarningsIds?.includes(earning.id)
            );

            return (
              <Checkbox
                className={styles.earnings_checkboxCell}
                checked={isChecked}
                partiallyChecked={!isFullyChecked}
                onChange={() => {
                  if (isChecked) {
                    setSelectedEarningsIds?.([]);
                  } else {
                    setSelectedEarningsIds?.((prev) => [
                      ...prev,
                      ...earnings.map((earning) => earning.id)
                    ]);
                  }
                }}
              />
            );
          },
          size: 1,
          minSize: 1,
          cell: ({ row }) => {
            const isSelected = selectedEarningsIds?.includes(
              earnings[row.index].id
            );

            return (
              <div
                className={styles.ordersTable_checkboxCell}
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  className={styles.earnings_checkboxCell}
                  checked={isSelected}
                  onChange={() => {
                    setSelectedEarningsIds?.((prev) =>
                      isSelected
                        ? prev.filter((id) => id !== earnings[row.index].id)
                        : [...prev, earnings[row.index].id]
                    );
                  }}
                />
              </div>
            );
          }
        },
        ...baseColumns,
        {
          id: "dotsColumn",
          size: 1,
          minSize: 1,
          cell: ({ row }) => {
            const actionsList: TableItemAction[] = [
              {
                label: t("Cancel"),
                Icon: (
                  <CrossCircleIcon fill={"#090914"} width={18} height={18} />
                ),
                onClick: () => {
                  setConfirmingEarningId(earnings[row.index].id);
                  openCancelEarningsModal();
                }
              }
            ];

            if (user.role === "Super") {
              actionsList.push({
                label: t("Show audit history"),
                Icon: <ClockIcon fill={"#090914"} width={18} height={18} />,
                onClick: () =>
                  navigate(
                    `/activity-log?entityType=Earning&entityId=${earnings[row.index].id}`
                  )
              });
            }

            return (
              <TableItemActionsControl
                actionsList={actionsList}
                rowIndex={row.index}
                setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
                openedItemOptionsIndex={openedItemOptionsIndex}
              />
            );
          }
        }
      ];

      const availableToBePaidTabColumns: Array<
        ColumnDef<EarningsListItem, EarningsListItem[]>
      > = [
        ...pendingTabColumns.slice(0, -1),
        {
          id: "pay",
          size: 5,
          minSize: 5,
          cell: ({ row }) => (
            <Button
              onClick={() => {
                setConfirmingEarningId(earnings[row.index].id);
                openPayEarningsModal();
              }}
            >
              Pay
            </Button>
          )
        },
        ...pendingTabColumns.slice(-1)
      ];

      switch (activeTab) {
        case EarningChapters.Pending:
          return pendingTabColumns;
        case EarningChapters.AvailableToBePaid:
          return availableToBePaidTabColumns;
        default:
          return baseColumns;
      }
    }, [
      earnings,
      selectedEarningsIds,
      openedItemOptionsIndex,
      activeTab,
      currentLanguage
    ]);

  return (
    <>
      <ItemsTable
        columns={columns}
        data={earnings}
        sorting={sorting}
        skeletonHeight={65}
        setSorting={setSorting}
        isLoading={isLoading}
        className={styles.earnings}
      />

      {isPayEarningsModalOpen && !!confirmingEarningId && (
        <PayEarningsModal
          earningIds={confirmingEarningId}
          isOpen={isPayEarningsModalOpen}
          onClose={closePayEarningsModal}
          onSuccess={() => {
            setSelectedEarningsIds?.((prev) =>
              prev.filter((id) => id !== confirmingEarningId)
            );
            refetchEarnings();
            setConfirmingEarningId(null);
          }}
        />
      )}

      {isCancelEarningsModalOpen && !!confirmingEarningId && (
        <CancelEarningsModal
          earningIds={confirmingEarningId}
          isOpen={isCancelEarningsModalOpen}
          onClose={closeCancelEarningsModal}
          onSuccess={() => {
            setSelectedEarningsIds?.((prev) =>
              prev.filter((id) => id !== confirmingEarningId)
            );
            refetchEarnings();
            setConfirmingEarningId(null);
          }}
        />
      )}
    </>
  );
};

export default EarningsTable;
